import React, { useState, useEffect, useRef } from "react";

import {
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { BlockStandard } from "components/admin/BlockStandard";
import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { OnboardingSelectorOptions } from "libLegacy/api/onboarding/onboardingModels";
import {
  createEnergyOperatorCredentials,
  fetchEnergyOperatorValues,
} from "libLegacy/api/onboarding/onboarding";
import { CustomSelect } from "components/onboarding/customSelect";
import { useToken } from "contexts/UserContext";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const NEXT_SCREEN = "green-energy-congrats";

export const GreenEnergySigned = ({ history }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hidePassword, setHidePassword] = useState(true);
  const token = useToken();
  const setGlobalError = useSetError();
  const errorInfo = useErrorInfo();
  const hasFormError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

  const [operators, setOperators] =
    useState<Array<OnboardingSelectorOptions>>();
  const [chosenOperatorId, setChosenOperatorId] =
    useState<OnboardingSelectorOptions["id"]>();

  const refEmail = useRef<HTMLInputElement>(null);
  const refPassword = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadSelectData = async (token) => {
      try {
        const data = await fetchEnergyOperatorValues(token);
        setOperators(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSelectData(token);
  }, [token, setGlobalError]);

  const handleSubmit = async () => {
    const email = refEmail.current?.value;
    const password = refPassword.current?.value;
    const desiredPlatform = operators?.find(
      ({ id }) => id === chosenOperatorId
    );

    if (email && password && desiredPlatform) {
      createCredentials({
        email,
        password,
        desiredPlatform,
        id: chosenOperatorId,
      });
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const createCredentials = async (formData) => {
    setIsLoading(true);
    try {
      await createEnergyOperatorCredentials(token, formData);
      history.push(NEXT_SCREEN);
    } catch (error) {
      // @ts-expect-error
      setGlobalError(error.response?.data?.error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleVisibility = () => {
    setHidePassword((prev) => !prev);
  };

  return (
    <SolutionsFrame
      buttonText="Próximo"
      onButtonClick={handleSubmit}
      skipTo={NEXT_SCREEN}
      isLoading={isLoading}
      currentStep={5}
      totalSteps={6}
    >
      <BlockStandard hideDivider>
        <img
          style={{ height: "15vh", objectFit: "contain" }}
          src="/static/img/solutions/signed.png"
          alt=""
        />
        <Typography variant="h2" align="center">
          Você concluiu sua assinatura
        </Typography>
        <Typography>
          Para garantirmos que os créditos de energia estão sendo computados na
          sua fatura e possamos calcular a sua economia, precisamos das suas
          credenciais de acesso ao site da sua distribuidora de energia.
        </Typography>
        <Typography>
          Nossos robôs coletam suas faturas e conferem todos os dados
          automaticamente.
        </Typography>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Typography variant="body2">
          Insira suas credenciais de acesso:
        </Typography>

        {operators && (
          <BlockStandard hideDivider>
            <CustomSelect
              value={chosenOperatorId}
              onChange={({ target: { value } }) => {
                setChosenOperatorId(value);
              }}
              options={operators}
            />
            <TextField
              required
              inputRef={refEmail}
              label="Seu login"
              variant="outlined"
              error={hasFormError && !refEmail.current?.value}
            />
            <TextField
              required
              inputRef={refPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleVisibility}>
                      {hidePassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              label="Senha de acesso"
              type={hidePassword ? "password" : "text"}
              variant="outlined"
              error={hasFormError && !refPassword.current?.value}
            />
          </BlockStandard>
        )}
      </BlockStandard>
    </SolutionsFrame>
  );
};
